import { useState } from "react";
import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { useApi } from "@aeriosiq/api-client";
import type {
  ExportFormat,
  ExportDataAcquisitionsResponse,
  GetDataAcquisitionsParams,
} from "@aeriosiq/types";
import { DownloadIcon } from "@aeriosiq/ui/icons";

export interface ExportButtonProps {
  /** The same params we send to /data-acquisitions — forwarded so the export
   * matches the current filtered + sorted view exactly. */
  params: GetDataAcquisitionsParams;
}

/**
 * Story 015 — Inspections list export.
 *
 * Toolbar button + menu with CSV / XLSX. On pick, GETs
 * `/data-acquisitions/export?format=...` with the current filter + sort params,
 * receives a presigned URL envelope, and triggers the download via an
 * anchor click (so the browser shows the Save-As dialog and uses the
 * filename from Content-Disposition).
 *
 * Disabled state with spinner during the request (AC7). Snackbar on
 * 4xx/5xx (AC6) — no partial download is ever triggered.
 */
export function ExportButton({ params }: ExportButtonProps) {
  const api = useApi();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePick = async (format: ExportFormat) => {
    handleClose();
    setBusy(true);
    setError(null);
    try {
      const response = await api.get<ExportDataAcquisitionsResponse>(
        "/data-acquisitions/export",
        {
          params: {
            format,
            q: params.q,
            state: params.state,
            inspection_type: params.inspection_type,
            date_from: params.date_from,
            date_to: params.date_to,
            sort_by: params.sort_by,
            sort_order: params.sort_order,
          },
          paramsSerializer: { indexes: null },
        },
      );
      // Anchor-click trigger so the browser respects the
      // Content-Disposition filename rather than reusing the URL slug.
      const a = document.createElement("a");
      a.href = response.data.url;
      a.download = response.data.filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError("Export failed — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        color="inherit"
        startIcon={
          busy ? (
            <CircularProgress size={14} />
          ) : (
            <DownloadIcon fontSize="small" />
          )
        }
        disabled={busy}
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
        aria-controls={anchorEl !== null ? "export-menu" : undefined}
      >
        Export
      </Button>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={handleClose}
        MenuListProps={{ "aria-label": "Export format" }}
      >
        <MenuItem
          onClick={() => {
            void handlePick("csv");
          }}
        >
          CSV
        </MenuItem>
        <MenuItem
          onClick={() => {
            void handlePick("xlsx");
          }}
        >
          XLSX
        </MenuItem>
      </Menu>
      <Snackbar
        open={error !== null}
        autoHideDuration={4000}
        onClose={() => {
          setError(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => {
            setError(null);
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
